import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent,
  Button, Chip, Skeleton, Container, IconButton, useMediaQuery, useTheme
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DevicesIcon from '@mui/icons-material/Devices';
import BoltIcon from '@mui/icons-material/Bolt';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MemoryIcon from '@mui/icons-material/Memory';
import SpeedIcon from '@mui/icons-material/Speed';
import BuildIcon from '@mui/icons-material/Build';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import useBehaviorTracking from '../hooks/useBehaviorTracking';
import { normalizeImageUrl, onImageErrorSetFallback } from '../utils/imageFallback';

const fixImageUrl = (url) => {
  return normalizeImageUrl(url, 'Product');
};

// Gemini 2.5 Pro - Tech color palette: dark, sleek, futuristic (GitHub-inspired)
const C = {
  bg: '#0d1117',
  surface: '#161b22',
  card: '#1c2128',
  border: '#30363d',
  accent: '#58a6ff',
  accentDark: '#388bfd',
  accentGlow: 'rgba(88,166,255,0.15)',
  green: '#3fb950',
  orange: '#d29922',
  red: '#f85149',
  purple: '#bc8cff',
  text: '#c9d1d9',
  textSecondary: '#8b949e',
  white: '#f0f6fc',
};

const TemplateTech = () => {
  const [featured, setFeatured] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [hotProducts, setHotProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBanner, setCurrentBanner] = useState(0);
  const { addToCart } = useCart();
  const { storeConfig, shippingConfig, categories: dbCategories } = useStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('common');
  useBehaviorTracking();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [featuredRes, newRes, hotRes] = await Promise.all([
          api.get('/products/featured?limit=8'),
          api.get('/products?sort=newest&limit=8'),
          api.get('/products?sort=sales&limit=8')
        ]);
        setFeatured(featuredRes.data || []);
        setNewProducts(newRes.data.items || []);
        setHotProducts(hotRes.data.items || []);
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const banners = [
    {
      title: storeConfig.store_name || t('template.tech_store'),
      subtitle: storeConfig.slogan || t('template.tech_slogan'),
      bgImage: '/images/banners/banner-tech.jpg',
    },
    {
      title: t('template.new_launch'),
      subtitle: t('template.new_launch_sub'),
    },
    {
      title: t('template.free_shipping_event'),
      subtitle: t('template.free_shipping_fee', { threshold: shippingConfig?.free_shipping_threshold || 799 }),
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentBanner(p => (p + 1) % banners.length), 5000);
    return () => clearInterval(timer);
  }, [banners.length]);

  // Gemini: glow border on hover, corner brackets visual, spec chips
  const ProductCard = ({ product }) => {
    const discount = product.original_price > product.sale_price
      ? Math.round((1 - product.sale_price / product.original_price) * 100) : 0;

    return (
      <Card sx={{
        height: '100%', display: 'flex', flexDirection: 'column',
        bgcolor: C.card, borderRadius: 2, overflow: 'hidden',
        transition: 'all 0.35s ease', border: `1px solid ${C.border}`,
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: C.accent,
          boxShadow: `0 0 20px ${C.accentGlow}, 0 8px 30px rgba(0,0,0,0.3)`,
          '& .product-img': { transform: 'scale(1.05)' },
        }
      }}>
        <Box sx={{ position: 'relative', pt: '100%', bgcolor: C.surface, cursor: 'pointer', overflow: 'hidden' }}
          onClick={() => navigate(`/products/${product.id}`)}>
          <CardMedia className="product-img" component="img" image={fixImageUrl(product.image_url)} alt={product.name} loading="lazy"
            onError={(e) => onImageErrorSetFallback(e, product.name)}
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', p: 2, transition: 'transform 0.4s ease' }} />
          {discount > 0 && (
            <Chip label={`-${discount}%`} size="small"
              sx={{ position: 'absolute', top: 10, left: 10, bgcolor: C.red, color: C.white, fontWeight: 700, fontSize: '0.65rem', height: 22, borderRadius: 1 }} />
          )}
          {/* Gemini: corner brackets */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: 20, height: 20, borderTop: `2px solid ${C.accent}40`, borderLeft: `2px solid ${C.accent}40` }} />
          <Box sx={{ position: 'absolute', top: 0, right: 0, width: 20, height: 20, borderTop: `2px solid ${C.accent}40`, borderRight: `2px solid ${C.accent}40` }} />
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: 20, height: 20, borderBottom: `2px solid ${C.accent}40`, borderLeft: `2px solid ${C.accent}40` }} />
          <Box sx={{ position: 'absolute', bottom: 0, right: 0, width: 20, height: 20, borderBottom: `2px solid ${C.accent}40`, borderRight: `2px solid ${C.accent}40` }} />
        </Box>
        <CardContent sx={{ flexGrow: 1, p: 2 }}>
          <Typography variant="body2" sx={{
            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: 40,
            color: C.text, fontWeight: 500, cursor: 'pointer',
            '&:hover': { color: C.accent }
          }} onClick={() => navigate(`/products/${product.id}`)}>{product.name}</Typography>
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ color: C.accent, fontWeight: 700, fontSize: '1.2rem', fontFamily: 'monospace' }}>
              ${product.sale_price ? Math.round(Number(product.sale_price)).toLocaleString() : 0}
            </Typography>
            {product.original_price > product.sale_price && (
              <Typography variant="caption" sx={{ color: C.textSecondary, textDecoration: 'line-through' }}>
                ${product.original_price ? Math.round(Number(product.original_price)).toLocaleString() : 0}
              </Typography>
            )}
          </Box>
          {/* Gemini: spec chips */}
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
            <Chip icon={<BoltIcon sx={{ fontSize: 11 }} />} label={t('template.quick_ship')} size="small"
              sx={{ height: 20, fontSize: '0.6rem', bgcolor: `${C.green}18`, color: C.green, border: `1px solid ${C.green}30`, '& .MuiChip-icon': { color: C.green } }} />
            {product.quantity <= 5 && product.quantity > 0 && (
              <Chip label={t('template.remaining_units', { count: product.quantity })} size="small"
                sx={{ height: 20, fontSize: '0.6rem', bgcolor: `${C.orange}18`, color: C.orange, border: `1px solid ${C.orange}30` }} />
            )}
          </Box>
        </CardContent>
        <Box sx={{ p: 2, pt: 0 }}>
          <Button fullWidth variant="contained" startIcon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
            onClick={() => addToCart(product.id)} disabled={product.quantity <= 0}
            sx={{
              bgcolor: C.accent, borderRadius: 1.5, textTransform: 'none',
              fontWeight: 700, py: 1, fontSize: '0.85rem',
              boxShadow: 'none',
              '&:hover': { bgcolor: C.accentDark, boxShadow: `0 4px 16px ${C.accentGlow}` },
              '&.Mui-disabled': { bgcolor: C.border, color: C.textSecondary },
            }}>
            {product.quantity > 0 ? t('btn.add_to_cart') : t('template.sold_out')}
          </Button>
        </Box>
      </Card>
    );
  };

  // Gemini: left-aligned section header with accent line, tech feel
  const SectionTitle = ({ icon, title, badge, badgeColor = C.red, action }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 3, height: 24, bgcolor: C.accent, borderRadius: 1 }} />
        {icon && <Box sx={{ color: C.accent, display: 'flex' }}>{icon}</Box>}
        <Typography variant="h6" sx={{ color: C.white, fontWeight: 700, letterSpacing: '0.03em' }}>{title}</Typography>
        {badge && <Chip label={badge} size="small" sx={{ bgcolor: badgeColor, color: C.white, fontWeight: 700, fontSize: '0.6rem', height: 20, borderRadius: 1 }} />}
      </Box>
      {action && <Button onClick={action.onClick} sx={{ color: C.accent, fontSize: '0.8rem' }}>{action.label} &gt;</Button>}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100vh' }}>
      {/* Category bar */}
      <Box sx={{ bgcolor: C.surface, py: 1.2, borderBottom: `1px solid ${C.border}`, overflowX: 'auto', '&::-webkit-scrollbar': { height: 0 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', minWidth: 'max-content' }}>
            <Chip icon={<DevicesIcon sx={{ fontSize: 16 }} />} label={t('template.all_items')} onClick={() => navigate('/products')}
              sx={{ bgcolor: C.accent, color: C.white, fontWeight: 600, '& .MuiChip-icon': { color: C.white } }} />
            {dbCategories.slice(0, 6).map(cat => (
              <Chip key={cat.name} label={cat.name} variant="outlined"
                onClick={() => navigate(`/products?category=${encodeURIComponent(cat.name)}`)}
                sx={{ borderColor: C.border, color: C.text, '&:hover': { borderColor: C.accent, bgcolor: `${C.accent}10` } }} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Gemini: 85vh hero with radial glow + grid lines + float animation */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', transition: 'transform 0.6s ease', transform: `translateX(-${currentBanner * 100}%)` }}>
          {banners.map((banner, idx) => (
            <Box key={idx} sx={{
              minWidth: '100%', bgcolor: C.bg,
              minHeight: { xs: 220, md: 360 },
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              position: 'relative', px: 3,
              backgroundImage: banner.bgImage ? `url(${banner.bgImage})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}>
              {/* grid line decorative background */}
              <Box sx={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: `linear-gradient(${C.border}18 1px, transparent 1px), linear-gradient(90deg, ${C.border}18 1px, transparent 1px)`,
                backgroundSize: '60px 60px', opacity: 0.4,
              }} />
              {/* radial glow */}
              <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: 200, md: 400 }, height: { xs: 200, md: 400 },
                background: `radial-gradient(circle, ${C.accentGlow} 0%, transparent 70%)`,
                borderRadius: '50%',
              }} />
              {/* float animation icon */}
              <MemoryIcon sx={{
                color: C.accent, fontSize: { xs: 32, md: 40 }, mb: 2, position: 'relative',
                animation: currentBanner === idx ? 'float 3s ease-in-out infinite, fadeIn 0.6s ease forwards' : 'none',
                opacity: currentBanner === idx ? 1 : 0,
              }} />
              <Typography sx={{
                color: C.white, fontWeight: 800,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
                textAlign: 'center', letterSpacing: { xs: 2, md: 4 }, position: 'relative',
                textTransform: 'uppercase', lineHeight: 1.1,
                animation: currentBanner === idx ? 'slideUp 0.7s ease 0.2s forwards' : 'none',
                opacity: currentBanner === idx ? 1 : 0,
              }}>{banner.title}</Typography>
              <Typography variant="body2" sx={{
                color: C.textSecondary, mt: 1.5, textAlign: 'center', position: 'relative',
                letterSpacing: '0.08em',
                animation: currentBanner === idx ? 'slideUp 0.7s ease 0.35s forwards' : 'none',
                opacity: currentBanner === idx ? 1 : 0,
              }}>{banner.subtitle}</Typography>
              <Button variant="contained" onClick={() => navigate('/products')}
                sx={{
                  mt: 3, bgcolor: C.accent, fontWeight: 700, borderRadius: 1.5,
                  px: 4, py: 1, position: 'relative',
                  boxShadow: `0 4px 20px ${C.accentGlow}`,
                  animation: currentBanner === idx ? 'slideUp 0.7s ease 0.5s forwards' : 'none',
                  opacity: currentBanner === idx ? 1 : 0,
                  '&:hover': { bgcolor: C.accentDark, boxShadow: `0 6px 28px rgba(88,166,255,0.3)` }
                }}>{t('btn.shop_now')}</Button>
            </Box>
          ))}
        </Box>
        <IconButton onClick={() => setCurrentBanner(p => (p - 1 + banners.length) % banners.length)}
          sx={{ position: 'absolute', left: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)', color: C.text, bgcolor: `${C.surface}cc`, '&:hover': { bgcolor: C.surface } }}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton onClick={() => setCurrentBanner(p => (p + 1) % banners.length)}
          sx={{ position: 'absolute', right: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)', color: C.text, bgcolor: `${C.surface}cc`, '&:hover': { bgcolor: C.surface } }}>
          <ArrowForwardIosIcon />
        </IconButton>
        <Box sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1.5 }}>
          {banners.map((_, i) => (
            <Box key={i} onClick={() => setCurrentBanner(i)}
              sx={{ width: currentBanner === i ? 24 : 8, height: 3, bgcolor: currentBanner === i ? C.accent : `${C.textSecondary}50`, cursor: 'pointer', transition: 'all 0.4s ease' }} />
          ))}
        </Box>
      </Box>

      {/* Info strip */}
      <Box sx={{ bgcolor: C.surface, py: 1.5, borderBottom: `1px solid ${C.border}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {[
              { icon: <LocalShippingIcon sx={{ fontSize: 16 }} />, text: t('template.free_shipping_threshold', { threshold: shippingConfig?.free_shipping_threshold || 799 }), color: C.green },
              { icon: <SecurityIcon sx={{ fontSize: 16 }} />, text: t('template.warranty'), color: C.accent },
              { icon: <SpeedIcon sx={{ fontSize: 16 }} />, text: t('template.quick_ship'), color: C.orange },
              { icon: <BuildIcon sx={{ fontSize: 16 }} />, text: t('template.tech_support'), color: C.purple },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Box sx={{ color: item.color }}>{item.icon}</Box>
                  <Typography variant="caption" sx={{ color: C.text, fontWeight: 500, letterSpacing: '0.03em' }}>{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        {/* Featured */}
        <SectionTitle icon={<BoltIcon />} title={t('template.featured_products')} badge="HOT" action={{ label: t('btn.view_all'), onClick: () => navigate('/products') }} />
        <Grid container spacing={2}>
          {loading ? Array(8).fill(0).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton variant="rectangular" sx={{ pt: '100%', borderRadius: 2, bgcolor: C.surface }} />
              <Skeleton width="60%" sx={{ mt: 1, bgcolor: C.surface }} />
              <Skeleton width="40%" sx={{ bgcolor: C.surface }} />
            </Grid>
          )) : featured.map(product => (
            <Grid item xs={6} sm={4} md={3} key={product.id}><ProductCard product={product} /></Grid>
          ))}
        </Grid>

        {/* Hot */}
        {hotProducts.length > 0 && (
          <Box sx={{ mt: { xs: 5, md: 8 } }}>
            <SectionTitle icon={<SpeedIcon />} title={t('template.hot_ranking_title')} badge="TOP" badgeColor={C.orange} />
            <Grid container spacing={2}>
              {hotProducts.slice(0, 4).map((product, idx) => (
                <Grid item xs={6} sm={4} md={3} key={product.id}>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{
                      position: 'absolute', top: 8, right: 8, width: 26, height: 26, zIndex: 2,
                      bgcolor: idx < 3 ? C.accent : C.textSecondary, color: C.bg,
                      borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: '0.75rem', fontFamily: 'monospace',
                    }}>#{idx + 1}</Box>
                    <ProductCard product={product} />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Gemini: tech specs showcase section */}
        <Box sx={{
          mt: { xs: 5, md: 8 }, p: { xs: 3, md: 5 },
          bgcolor: C.surface, borderRadius: 2, border: `1px solid ${C.border}`,
          position: 'relative', overflow: 'hidden',
        }}>
          {/* decorative grid */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            backgroundImage: `linear-gradient(${C.border}15 1px, transparent 1px), linear-gradient(90deg, ${C.border}15 1px, transparent 1px)`,
            backgroundSize: '40px 40px', opacity: 0.3,
          }} />
          <Grid container spacing={4} sx={{ position: 'relative' }}>
            <Grid item xs={12} md={5}>
              <Typography variant="caption" sx={{ color: C.accent, letterSpacing: '0.15em', display: 'block', mb: 2 }}>WHY CHOOSE US</Typography>
              <Typography variant="h5" sx={{ color: C.white, fontWeight: 700, mb: 2, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                {t('template.pro_tech_trust')}
              </Typography>
              <Typography variant="body2" sx={{ color: C.textSecondary, lineHeight: 1.8, mb: 3 }}>
                {t('template.pro_tech_desc')}
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/products')}
                sx={{ borderColor: C.accent, color: C.accent, borderRadius: 1.5, '&:hover': { bgcolor: C.accent, color: C.bg } }}>
                {t('btn.explore_all')}
              </Button>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {[
                  { icon: <SecurityIcon />, title: t('template.warranty'), desc: t('template.warranty_desc'), color: C.accent },
                  { icon: <LocalShippingIcon />, title: t('template.fast_delivery'), desc: t('template.fast_delivery_full'), color: C.green },
                  { icon: <BuildIcon />, title: t('template.tech_support'), desc: t('template.tech_support_desc'), color: C.orange },
                  { icon: <SupportAgentIcon />, title: t('template.after_sales_service'), desc: storeConfig.service_hours || t('template.service_hours_short'), color: C.purple },
                ].map((item, i) => (
                  <Grid item xs={6} key={i}>
                    <Box sx={{
                      p: 2.5, bgcolor: `${C.card}`, borderRadius: 2,
                      border: `1px solid ${C.border}`, textAlign: 'center',
                      transition: 'border-color 0.3s',
                      '&:hover': { borderColor: item.color },
                    }}>
                      <Box sx={{ color: item.color, mb: 1, '& svg': { fontSize: 28 } }}>{item.icon}</Box>
                      <Typography variant="subtitle2" sx={{ color: C.white, fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                      <Typography variant="caption" sx={{ color: C.textSecondary }}>{item.desc}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* New */}
        {newProducts.length > 0 && (
          <Box sx={{ mt: { xs: 5, md: 8 } }}>
            <SectionTitle icon={<DevicesIcon />} title={t('template.new_arrivals_tech')} badge="NEW" badgeColor={C.green} />
            <Grid container spacing={2}>
              {newProducts.slice(0, 4).map(product => (
                <Grid item xs={6} sm={4} md={3} key={`new-${product.id}`}><ProductCard product={product} /></Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>

      {/* CSS Animations */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(25px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
      `}</style>
    </Box>
  );
};

export default TemplateTech;



